import React, { useState, useEffect } from "react";

// Components
import Projects from "./Projects/Projects";
import Tasks from "./Tasks/Tasks";
import WorkOn from "./WorkOn/WorkOn";
import Background from "./Components/Background";
import Switch from "./Components/Switch";
import Alert from "./Components/Alert";

// Variables
import {
  ProjectData,
  TaskTemplate,
  ProjectTemplate,
  SessionTemplate,
} from "../variables";
import { getHoursFromMs, getMinutesFromMs } from "../helpers";

// Others
import { v4 as uuidv4 } from "uuid";

const Home = () => {
  // State
  const [projects, setProjects] = useState(ProjectData ? ProjectData : []);
  const [activeProject, setActiveProject] = useState();
  const [activeTask, setActiveTask] = useState();
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [alert, setAlert] = useState({
    message: "",
    show: false,
    type: "error",
  });

  // Form state
  const [showProjectForm, setShowProjectForm] = useState(false);
  const [showTaskForm, setShowTaskForm] = useState(false);

  // DARK AND LIGHT THEME
  // On page load or when changing themes, best to add inline in `head` to avoid FOUC
  const updateTheme = () => {
    if (isDarkMode) document.documentElement.classList.add("dark");
    else document.documentElement.classList.remove("dark");
  };
  useEffect(updateTheme, [isDarkMode]);

  // Close alert after certain time
  useEffect(() => {
    if (alert.show) {
      setTimeout(() => setAlert({ ...alert, show: false }), 4500);
    }
  }, [alert]);

  // Classes
  const baseClasses = "h-full grow flex flex-col";

  // Theme classes
  const lightThemeClasses = "";
  const darkThemeClasses = " ";

  // Combine classes
  const defaultAppClasses = [baseClasses, darkThemeClasses, lightThemeClasses];

  // PROJECT ACTIONS
  // ADD PROJECT
  const onAddProject = (name, type) => {
    setIsLoading(true);
    let updatedProjects = [...projects];

    // Validation
    if (!name) {
      setIsLoading(false);
      return setAlert({
        message: "Please fill in project name",
        show: true,
        type: "error",
      });
    }
    if (!type) {
      setIsLoading(false);
      return setAlert({
        message: "Please fill in project type",
        show: true,
        type: "error",
      });
    }

    // Build project object
    let newProject = {
      ...ProjectTemplate,
      _ID: uuidv4(),
      _Name: name,
      _Type: type,
    };

    // Add project to the start
    updatedProjects.push(newProject);

    // Update state
    setShowProjectForm(false);
    setProjects(updatedProjects);
    setActiveProject(newProject);
    setIsLoading(false);

    // Alert user
    return setAlert({
      message: "Successfully created project",
      show: true,
      type: "success",
    });
  };

  // TASK ACTIONS
  // ADD TASK
  const onAddTask = (newTaskName) => {
    setIsLoading(true);

    // Validation
    if (!newTaskName || newTaskName === "") {
      setIsLoading(false);
      return setAlert({
        message: "Please fill in task name",
        show: true,
        type: "error",
      });
    }

    let updatedProject = { ...activeProject };
    // Add new task
    updatedProject._Tasks = [
      { ...TaskTemplate, _ID: uuidv4(), _Title: newTaskName },
      ...updatedProject._Tasks,
    ];

    // Close pop up
    setShowTaskForm(false);

    // Update active project state
    setActiveProject(updatedProject);
    setIsLoading(false);
  };

  // ADD SESSION
  const onFinishSession = (time) => {
    let hours = getHoursFromMs(time);
    let minutes = getMinutesFromMs(time);

    // Convert time to object
    let newSession = {
      ...SessionTemplate,
      _ID: uuidv4(),
      _TimeWorkedOn: {
        _Hours: hours,
        _Minutes: minutes,
      },
    };

    const getSumOfTimeFromSessions = (sessions) => {
      let hours = 0;
      let minutes = 0;
      sessions.forEach((session) => {
        hours = hours + session._TimeWorkedOn._Hours;
        minutes = minutes + session._TimeWorkedOn._Minutes;
      });

      return [hours, minutes];
    };

    // Calculate worked on time
    let workedOn = { ...activeTask._TimeWorkedOn };
    let [sumHours, sumMinutes] = getSumOfTimeFromSessions([
      newSession,
      ...activeTask._Sessions,
    ]);

    workedOn = { _Hours: sumHours, _Minutes: sumMinutes };

    // Add session to updated task object
    let updatedActiveTask = {
      ...activeTask,
      _TimeWorkedOn: workedOn,
      _Sessions: [newSession, ...activeTask._Sessions],
    };

    // Updated project
    let updatedActiveProject = {
      ...activeProject,
      _Tasks: [
        ...activeProject._Tasks.map((t) => {
          if (t._ID === updatedActiveTask._ID) return updatedActiveTask;
          else return t;
        }),
      ],
    };

    // Updated projects
    let updatedProjects = [
      ...projects.map((p) => {
        if (p._ID === updatedActiveProject._ID) return updatedActiveProject;
        else return p;
      }),
    ];

    // Set state
    setProjects(updatedProjects);
    setActiveTask(updatedActiveTask);
    setActiveProject(updatedActiveProject);
  };
  const onUpdateProject = (updatedProject) => {
    if (activeTask) {
      let updatedActiveTask = updatedProject._Tasks.find(
        (task) => task._ID === activeTask._ID
      );
      setActiveTask(updatedActiveTask);
    }
    setActiveProject(updatedProject);
  };

  const hideAllForms = () => {
    setShowProjectForm(false);
    setShowTaskForm(false);
  };

  return (
    <Background onClick={hideAllForms}>
      {/* Alert */}
      <Alert
        show={alert.show}
        message={alert.message}
        type={alert.type}
        onClose={() => setAlert({ ...alert, show: false })}
        className="grow-0"
      />
      <div onClick={hideAllForms} className={defaultAppClasses.join(" ")}>
        {/* Dark mode switch */}
        <div className="flex w-100 right-0 absolute mr-10">
          <p className="mr-2">{!isDarkMode ? "Dark" : "Light"} mode</p>
          <Switch toggle={isDarkMode} setToggle={setIsDarkMode} />
        </div>
        <div className="flex grow justify-center h-full mt-3">
          <div className="w-1.5/6 mr-9">
            <Projects
              projects={projects}
              activeProject={activeProject}
              setActiveProject={setActiveProject}
              showProjectForm={showProjectForm}
              onAddProject={onAddProject}
              onUpdateProjects={(projects) => setProjects(projects)}
              isLoading={isLoading}
              setShowProjectForm={(value) => {
                setShowTaskForm(false);
                setShowProjectForm(value);
              }}
            />
          </div>
          <div className="w-3/6 mr-9">
            <Tasks
              activeProject={activeProject}
              activeTask={activeTask}
              onChangeActiveTask={(task) => {
                if (task === activeTask) {
                  setActiveTask();
                } else {
                  setActiveTask(task);
                }
              }}
              showTaskForm={showTaskForm}
              setShowTaskForm={(value) => {
                setShowProjectForm(false);
                setShowTaskForm(value);
              }}
              onFormAction={onAddTask}
              onUpdateProject={onUpdateProject}
            />
          </div>
          <div className="w-1.5/6 ">
            <WorkOn activeTask={activeTask} onFinishSession={onFinishSession} />
          </div>
        </div>
      </div>
    </Background>
  );
};

export default Home;
